class CartDrawer {
  constructor() {
    this.selectors = {
      drawer: '#cart-drawer',
      content: '.cart-drawer-content',
      trigger: '#cart-drawer-trigger',
      closeBtn: '#cart-drawer-close',
      continueBtn: '#cart-continue-shopping',
      itemsContainer: '#cart-drawer-items-container',
      footer: '#cart-drawer-footer',
    }

    this.drawer = document.querySelector(this.selectors.drawer)
    this.previousItemCount = 0
    this.previousProductIds = new Set()
    this.isUpdating = false
    this.init()
  }

  init() {
    if (!this.drawer) return
    this.attachGlobalListeners()
    this.attachDrawerListeners()
    this.updateCartState()
  }

  updateCartState() {
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        this.previousItemCount = cart.item_count
        this.previousProductIds = new Set(cart.items.map((item) => item.product_id))
      })
      .catch(() => {})
  }

  attachGlobalListeners() {
    const trigger = document.querySelector(this.selectors.trigger)
    if (trigger) {
      trigger.addEventListener('click', (e) => {
        e.preventDefault()
        this.open()
      })
    }

    this.drawer.addEventListener('click', (e) => {
      if (e.target === this.drawer) this.close()
    })
  }

  attachDrawerListeners() {
    const content = this.drawer.querySelector(this.selectors.content)

    if (content) {
      const discountInput = content.querySelector('#discount-code')
      if (discountInput) {
        discountInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            this.applyDiscount()
          }
        })
      }

      content.addEventListener('click', (e) => {
        if (
          e.target.closest(this.selectors.closeBtn) ||
          e.target.closest(this.selectors.continueBtn)
        ) {
          this.close()
          return
        }

        const qtyBtn = e.target.closest('.js-qty-btn')
        if (qtyBtn) {
          e.preventDefault()

          if (qtyBtn.disabled || this.isUpdating) return

          const input = qtyBtn.parentElement.querySelector('input')
          const value = parseInt(input.value)
          const key = input.dataset.key
          const action = qtyBtn.dataset.action

          if (action === 'plus') {
            const limit = input.dataset.limit ? parseInt(input.dataset.limit) : Infinity
            if (value < limit) {
              this.updateQuantity(key, value + 1, 'quantity_change')
            }
          } else if (action === 'minus') {
            this.updateQuantity(
              key,
              value - 1,
              value - 1 === 0 ? 'item_removed' : 'quantity_change'
            )
          }
          return
        }

        const removeBtn = e.target.closest('.js-remove-btn')
        if (removeBtn) {
          e.preventDefault()
          if (this.isUpdating) return
          const key = removeBtn.dataset.key
          this.updateQuantity(key, 0, 'item_removed')
          return
        }

        const applyDiscountBtn = e.target.closest('#apply-discount')
        if (applyDiscountBtn) {
          e.preventDefault()
          e.stopPropagation()
          this.applyDiscount()
          return
        }

        const removeDiscountBtn = e.target.closest('.js-remove-discount')
        if (removeDiscountBtn) {
          e.preventDefault()
          this.removeDiscount()
          return
        }
      })
    }
  }

  open() {
    this.drawer.style.display = 'block'
    document.body.classList.add('cart-open')
    setTimeout(() => {
      this.drawer.classList.add('active')
    }, 10)
  }

  close() {
    this.drawer.classList.remove('active')
    setTimeout(() => {
      this.drawer.style.display = 'none'
      document.body.classList.remove('cart-open')
    }, 150)
  }

  updateQuantity(key, quantity, changeType = 'quantity_change') {
    if (this.isUpdating) return

    this.isUpdating = true
    const body = JSON.stringify({
      id: key,
      quantity: quantity,
      sections: 'header',
    })

    fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: body,
    })
      .then((response) => response.json())
      .then((state) => {
        this.renderCart(state)
        this.updateCartQuantities(state)

        const currentProductIds = new Set(state.items.map((item) => item.product_id))

        const setsAreEqual = (set1, set2) => {
          if (set1.size !== set2.size) return false
          for (const item of set1) {
            if (!set2.has(item)) return false
          }
          return true
        }

        let productsChanged = false

        if (changeType === 'quantity_change') {
          productsChanged = false
        } else {
          productsChanged =
            changeType === 'item_removed' ||
            changeType === 'item_added' ||
            !setsAreEqual(currentProductIds, this.previousProductIds)
        }

        this.previousItemCount = state.item_count
        this.previousProductIds = currentProductIds

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: {
              cart: state,
              changeType: changeType,
              productsChanged: productsChanged,
            },
          })
        )

        this.isUpdating = false
      })
      .catch((error) => {
        console.error(error)
        this.isUpdating = false
      })
  }

  applyDiscount() {
    const discountInput = this.drawer.querySelector('#discount-code')
    const discountMessage = this.drawer.querySelector('#discount-message')
    const accordionContent = this.drawer.querySelector('#discount-accordion-content')
    const chevron = this.drawer.querySelector('#discount-chevron')
    const code = discountInput ? discountInput.value.trim() : ''

    const showError = (message) => {
      if (discountMessage) {
        discountMessage.innerHTML = `<span class="flex items-center gap-1"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg> ${message}</span>`
        discountMessage.classList.remove('hidden')
      }
      if (accordionContent) {
        accordionContent.style.maxHeight = 'none'
        const newHeight = accordionContent.scrollHeight + 40
        accordionContent.style.maxHeight = newHeight + 'px'
        accordionContent.style.opacity = '1'
      }
      if (chevron) {
        chevron.style.transform = 'rotate(180deg)'
      }
    }

    if (!code) {
      showError('Escribe un código.')
      return
    }

    if (discountMessage) discountMessage.classList.add('hidden')

    const body = JSON.stringify({
      discount: code,
      sections: 'header',
    })

    fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: body,
    })
      .then((response) => response.json())
      .then((state) => {
        if ((state.errors && state.errors.discount) || state.status === 'error') {
          showError('Código no válido.')
          return
        }

        const hasDiscount =
          state.total_discount > 0 ||
          (state.discount_applications && state.discount_applications.length > 0) ||
          (state.cart_level_discount_applications &&
            state.cart_level_discount_applications.length > 0)

        if (!hasDiscount) {
          showError('No aplica o expiró.')
          return
        }

        if (discountInput) discountInput.value = ''
        if (discountMessage) discountMessage.classList.add('hidden')

        this.renderCart(state)
        this.updateCartQuantities(state)

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: {
              cart: state,
              changeType: 'discount_applied',
              productsChanged: false,
            },
          })
        )
      })
      .catch((error) => {
        console.error('Error:', error)
        showError('Intenta de nuevo.')
      })
  }

  removeDiscount() {
    const body = JSON.stringify({
      discount: '',
      sections: 'header',
    })

    fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: body,
    })
      .then((response) => response.json())
      .then((state) => {
        this.renderCart(state)
        this.updateCartQuantities(state)
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: {
              cart: state,
              changeType: 'discount_removed',
              productsChanged: false,
            },
          })
        )
      })
      .catch((error) => {
        console.error('Error removing discount:', error)
      })
  }

  updateCartQuantities(state) {
    if (!state.items) return

    const newQuantities = {}
    state.items.forEach((item) => {
      newQuantities[item.variant_id] = item.quantity
    })

    window.cartQuantities = newQuantities
    window.cartSubtotal = state.total_price || 0

    if (window.updateFreeShippingProgress) {
      window.updateFreeShippingProgress(window.cartSubtotal)
    }
  }

  renderCart(state) {
    if (state && state.sections && state.sections['header']) {
      try {
        const headerHtml = state.sections['header']
        const html = new DOMParser().parseFromString(headerHtml, 'text/html')

        const newDrawerContent = html.querySelector(this.selectors.content)
        const currentDrawerContent = this.drawer.querySelector(this.selectors.content)

        if (currentDrawerContent && newDrawerContent) {
          const currentRecsWrapper = currentDrawerContent.querySelector('#cart-rec-section-wrapper')
          const currentRecsSection = currentDrawerContent.querySelector('#cart-rec-section')

          let recsElement = null

          const isRecsVisible = currentRecsSection && currentRecsSection.style.display !== 'none'

          if (isRecsVisible) {
            if (currentRecsWrapper) {
              recsElement = currentRecsWrapper.cloneNode(true)
            } else if (currentRecsSection) {
              recsElement = currentRecsSection.cloneNode(true)
            }
          }

          currentDrawerContent.innerHTML = newDrawerContent.innerHTML

          if (recsElement) {
            const newForm = currentDrawerContent.querySelector('#cartForm')

            if (newForm) {
              const itemsSection = newForm.querySelector('.space-y-4.mb-6')

              if (itemsSection) {
                itemsSection.insertAdjacentElement('afterend', recsElement)
              } else {
                newForm.appendChild(recsElement)
              }
            } else {
              const newItemsContainer = currentDrawerContent.querySelector(
                '#cart-drawer-items-container'
              )
              if (newItemsContainer) {
                newItemsContainer.appendChild(recsElement)
              }
            }
          }

          this.attachDrawerListeners()

          fetch('/cart.js')
            .then((r) => r.json())
            .then((cart) => {
              this.updateCartQuantities(cart)
              if (typeof window.updateFreeShippingProgress === 'function') {
                window.updateFreeShippingProgress(cart.total_price)
              }
            })
        }

        const currentTrigger = document.querySelector(this.selectors.trigger)
        const newTrigger = html.querySelector(this.selectors.trigger)

        if (currentTrigger && newTrigger) {
          currentTrigger.innerHTML = newTrigger.innerHTML
        }
      } catch (e) {
        console.error('CartDrawer: Error in renderCart', e)
        this.refreshDrawer()
      }
    } else {
      this.refreshDrawer()
    }
  }

  refreshDrawer() {
    fetch(window.location.pathname + window.location.search)
      .then((r) => r.text())
      .then((html) => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')

        const headerContent = doc.querySelector('#header') || doc.querySelector('header')
        if (!headerContent) {
          return
        }

        const newDrawerContent = headerContent.querySelector(this.selectors.content)
        const currentDrawerContent = this.drawer.querySelector(this.selectors.content)

        if (currentDrawerContent && newDrawerContent) {
          currentDrawerContent.innerHTML = newDrawerContent.innerHTML
          this.attachDrawerListeners()
        }

        const currentTrigger = document.querySelector(this.selectors.trigger)
        const newTrigger = headerContent.querySelector(this.selectors.trigger)

        if (currentTrigger && newTrigger) {
          currentTrigger.innerHTML = newTrigger.innerHTML
        }

        fetch('/cart.js')
          .then((r) => r.json())
          .then((cart) => {
            this.updateCartQuantities(cart)
            if (typeof window.updateFreeShippingProgress === 'function') {
              window.updateFreeShippingProgress(cart.total_price)
            }
          })
      })
      .catch((e) => console.error('Error refreshing drawer:', e))
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.routes = window.routes || {
    cart_add_url: '/cart/add',
    cart_change_url: '/cart/change',
    cart_update_url: '/cart/update',
    cart_url: '/cart',
  }

  window.cartDrawerInstance = new CartDrawer()
})
